import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { getTopupInProgressRequest, topupLoadApiUrl } from '@epi-api/topupLoad';
import { fetchPost as Post } from '@epi-helpers/FetchHelpers';
import { showNotification } from '@epi-actions/overlays';
import { getBenefitGroupSettings } from '@epi-actions/api';
import { FormFields } from '@epi-forms/helpers';
import { formatDate } from '@epi-pages/Reports/utils';
import { TopupFormType } from '@epi-pages/LoadBenefit/components/Topup/TopupForm';
import { useOnboardingWizard } from '@epi-shared/hooks';
import { TopupFormStatuses } from '@epi-constants/topupForm';

export const useTopupRequest = (
  setShowConfirmationModal,
  isToggle,
  showOnHoldBtn,
  setTopupRequestStatus
) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { watch } = useFormContext<TopupFormType>();
  const [isTopupPending, setIsTopupPending] = useState(false);
  const [isTopupProcessing, setIsTopupProcessing] = useState(false);
  const [summaryId, setSummaryId] = useState<string | undefined>(undefined);
  const timerRef = useRef<null | number>(null);
  const { isOnBoardingActive } = useOnboardingWizard();

  const scheduleDate = watch(FormFields.ScheduleDate);

  const getTopupInProgress = async () => {
    const response = await getTopupInProgressRequest();
    if (!response) return;
    setIsTopupProcessing(response.isTopUpLoadInProgress);
    if (response.ids?.length) {
      setSummaryId(response.ids[0]);
    }

    if (response.isTopUpLoadInProgress) {
      timerRef.current = window.setTimeout(() => {
        getTopupInProgress();
      }, 3000);
    }
  };

  const postTopupRequest = payload => {
    setIsTopupPending(true);
    setTopupRequestStatus(null);
    if (!isOnBoardingActive) {
      setShowConfirmationModal(false);
    }

    const onSuccess = async response => {
      setSummaryId(response.topUpLoadId);
      setIsTopupProcessing(true);
      setIsTopupPending(false);
      setTopupRequestStatus(TopupFormStatuses.SUCCESS);
    };
    const onFailure = error => {
      const errorCode =
        error?.content?.errors?.length > 0
          ? error.content.errors[0].code
          : null;

      if (errorCode && t(`messages.${errorCode}`)) {
        dispatch(
          showNotification({ message: `messages.${errorCode}` }, 'error')
        );
      } else {
        dispatch(showNotification({ message: 'messages.error' }, 'error'));
      }

      setIsTopupPending(false);
      setTopupRequestStatus(TopupFormStatuses.FAIL);
    };
    return Post(
      topupLoadApiUrl,
      {
        ...payload,
        showOnHoldBeneficiaries: showOnHoldBtn ? isToggle : null,
        scheduledOn: formatDate(scheduleDate)
      },
      onSuccess
    ).catch(onFailure);
  };

  useEffect(() => {
    dispatch(getBenefitGroupSettings());
  }, []);

  useEffect(() => {
    getTopupInProgress();
  }, [isTopupProcessing]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    postTopupRequest,
    isTopupPending,
    isTopupProcessing,
    summaryId
  };
};
